// ============================================================================
// Section 2 — From booking to completed clean (Sticky Steps timeline)
// Scroll-activated highlighting, animated progress line, framer-motion enter.
// ============================================================================

import { useRef, useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Calendar, Users, CheckSquare, Activity, Wrench } from "lucide-react";

const STEPS = [
  {
    title: "Auto-create turnovers",
    body: "Every reservation becomes a scheduled cleaning task with the right checklist and due time.",
    icon: Calendar,
  },
  {
    title: "Assign the right people",
    body: "Owners invite team admins. Team admins add workers and delegate tasks inside the property.",
    icon: Users,
  },
  {
    title: "Standardize quality",
    body: "Room-by-room checklists, photo requirements, and quick checks — consistent every turnover.",
    icon: CheckSquare,
  },
  {
    title: "Track progress live",
    body: "See task status, photo uploads, inventory updates, and issues as they happen.",
    icon: Activity,
  },
  {
    title: "Handle maintenance fast",
    body: "Cleaners can flag issues mid-task so nothing gets missed between guests.",
    icon: Wrench,
  },
];

const STEP_COUNT = STEPS.length;

export default function StickyStepsSection() {
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const sectionRef = useRef<HTMLElement | null>(null);
  const reducedMotion = useReducedMotion();

  // IntersectionObserver: active step = card with highest intersection ratio
  const ratiosRef = useRef<Record<number, number>>({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = cardRefs.current.indexOf(entry.target as HTMLDivElement);
          if (index !== -1) ratiosRef.current[index] = entry.intersectionRatio;
        });
        const ratios = ratiosRef.current;
        let bestIndex = 0;
        let bestRatio = 0;
        for (let i = 0; i < STEP_COUNT; i++) {
          const r = ratios[i] ?? 0;
          if (r > bestRatio) {
            bestRatio = r;
            bestIndex = i;
          }
        }
        if (bestRatio > 0.08) setActiveStepIndex(bestIndex);
      },
      { root: null, rootMargin: "-15% 0px -45% 0px", threshold: [0.25, 0.4, 0.5, 0.6, 0.75] }
    );

    const refs = cardRefs.current;
    const timer = setTimeout(() => {
      refs.forEach((el) => {
        if (el) observer.observe(el);
      });
    }, 100);
    return () => {
      clearTimeout(timer);
      refs.forEach((el) => el && observer.unobserve(el));
    };
  }, []);

  const progressPercent = STEP_COUNT > 1 ? (activeStepIndex / (STEP_COUNT - 1)) * 100 : 0;

  const cardVariants = {
    hidden: { opacity: 0, y: reducedMotion ? 0 : 20 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, delay: i * 0.08 },
    }),
  };

  return (
    <section
      id="how-it-works"
      ref={sectionRef}
      className="border-t border-border/60 bg-muted/30 py-16 sm:py-20"
      aria-labelledby="sticky-steps-heading"
    >
      <div className="mx-auto max-w-6xl px-6">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">
          {/* Left: sticky headline + subheadline — sticks while scrolling this section, then scrolls away with it */}
          <div className="lg:max-w-[480px] lg:min-h-0">
            <div className="lg:sticky lg:top-24 lg:pb-8">
              <h2
                id="sticky-steps-heading"
                className="text-3xl font-bold leading-tight tracking-tight text-foreground sm:text-4xl"
              >
                From booking to completed clean
              </h2>
              <p className="mt-4 text-muted-foreground sm:text-lg">
                Connect your calendar once. Stravo auto-creates turnover tasks, enforces checklists, and keeps your
                entire team aligned — from owner to cleaner.
              </p>
            </div>
          </div>

          {/* Right: timeline wrapper + line + progress + cards */}
          <div className="relative pl-12 sm:pl-14">
            {/* Vertical track line (full height) */}
            <div
              className="absolute top-0 bottom-0 w-px bg-border/80"
              style={{ left: "1.375rem" }}
              aria-hidden
            />
            {/* Progress fill overlay */}
            <div
              className="absolute w-px bg-primary transition-[height] duration-500 ease-out"
              style={{
                left: "1.375rem",
                top: 0,
                height: `${progressPercent}%`,
              }}
              aria-hidden
            />

            <ul className="flex flex-col gap-6 sm:gap-8" aria-label="Steps">
              {STEPS.map((step, index) => {
                const Icon = step.icon;
                const isActive = index === activeStepIndex;
                return (
                  <motion.li
                    key={step.title}
                    custom={index}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-50px 0px -100px 0px" }}
                    variants={cardVariants}
                    className="relative flex gap-4"
                  >
                    {/* Node dot on the line (center at 1.375rem) */}
                    <div
                      className="absolute flex h-6 w-6 shrink-0 items-center justify-center"
                      style={{ left: "0.5rem", top: "1.5rem" }}
                      aria-hidden
                    >
                      <span
                        className={`block rounded-full transition-all duration-300 ${
                          isActive
                            ? "h-3 w-3 border-2 border-primary bg-primary shadow-[0_0_0_4px_hsl(var(--primary)/0.2)]"
                            : "h-2.5 w-2.5 border-2 border-border bg-background"
                        }`}
                      />
                    </div>

                    {/* Card */}
                    <div
                      ref={(el) => {
                        cardRefs.current[index] = el;
                      }}
                      className={`relative ml-2 flex flex-1 gap-4 rounded-2xl border bg-white/70 p-6 shadow-sm backdrop-blur transition-all duration-300 ${
                        isActive
                          ? `${reducedMotion ? "" : "scale-[1.02]"} border-primary/30 shadow-lg`
                          : "border-border/80"
                      }`}
                    >
                      <div
                        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl transition-all duration-300 ${
                          isActive ? "bg-primary/15 text-primary shadow-sm" : "bg-muted/80 text-muted-foreground"
                        }`}
                        aria-hidden
                      >
                        <Icon className="h-6 w-6" strokeWidth={1.5} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-lg font-semibold text-foreground">{step.title}</h3>
                        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{step.body}</p>
                      </div>
                    </div>
                  </motion.li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
